import {
  pickedCompletion,
  startCompletion,
  snippetCompletion,
} from '@codemirror/autocomplete';

/**
 * Build the `apply` function used for applying an autocompletion
 * @param {object} options
 * @param {string} options.content
 *    The text to insert.
 * @param {string} [options.parentCloseOperator]
 *    The closing half of the parent operator. It is used to check if we're
 *    inside the parent operators so we can move the cursor out and append the
 *    contentOperators after it.
 * @param {string} [options.contentOperators]
 *    The operators to be inserted after the content to trigger the next part of
 *    the expression, or complete the function call.
 * @param {number} [options.cursorShift=0]
 *    How many space to shift the cursor after the content is inserted.
 *    Negative values moved the cursor left, and positive right. This is only
 *    applied if the contentOperators are inserted.
 * @param {boolean} [options.triggerAgain=false]
 *    Should the autocomplete popup be triggered again after insertion.
 */
const applyFnComplex = ({
  content,
  parentCloseOperator,
  contentOperators,
  cursorShift,
  triggerAgain = false,
}) => (view, completion, from, to) => {
  // Get the text immediately following the cursor
  const textAfterCursor = view.state.sliceDoc(
    to,
    to + (!!parentCloseOperator ? parentCloseOperator.length : 0) + 1,
  );

  // Can we overwrite or add the parentCloseOperator in the current position
  let insertParent = null,
    // Can we safely insert contentOperators in the current position
    insertOperators = false;

  // If we're inside a parent operator
  if (!!parentCloseOperator) {
    // If we're immediately inside the parent operator, we can overwrite it so
    // we can add any necessary contentOperators.
    if (textAfterCursor.startsWith(parentCloseOperator)) {
      insertParent = 'overwrite';
      // Add contentOperators if provided and the text after the
      // parentCloseOperator doesn't already match the start of the
      // contentOperators
      if (
        !!contentOperators &&
        contentOperators[0] !== textAfterCursor[parentCloseOperator.length]
      ) {
        insertOperators = true;
      }
    }
    // If we're at the end of the input, or before a space, we can add the
    // parentCloseOperator to close the parent.
    else if (!textAfterCursor[0] || textAfterCursor[0] === ' ') {
      insertParent = 'add';
      // Insert contentOperators if provided since next character is empty
      insertOperators = !!contentOperators;
    }
    // Otherwise we are in an unknown state so we won't use the
    // parentCloseOperator
  }
  // Otherwise, add contentOperators if provided and the text after the
  // cursor doesn't already match the start of the contentOperators
  else if (!!contentOperators && contentOperators[0] !== textAfterCursor[0]) {
    insertOperators = true;
  }

  // Build insertion string
  const insert = `${content}${!!insertParent ? parentCloseOperator : ''}${
    insertOperators ? contentOperators : ''
  }`;
  // Update to position if needed
  const updatedTo =
    to + (insertParent === 'overwrite' ? parentCloseOperator.length : 0);
  // Determine final cursor position
  const shift = from + insert.length + (insertOperators ? cursorShift : 0);

  // Dispatch a state update
  view.dispatch(
    view.state.update({
      // Add the necessary annotation for the transaction
      annotations: [pickedCompletion],
      // Define the changes and where they should happen
      changes: { from, to: updatedTo, insert },
      // Define the position of the cursor after the change
      selection: { anchor: shift },
    }),
  );
  // Trigger the autocomplete popup if needed
  if (triggerAgain && insertOperators) startCompletion(view);
};

/**
 * Maps a binding object to the properties that are used by the autocompletion
 * code, and adds an apply function that will insert the content in a smart way,
 * adding any necessary operators after (like parens or brackets), which are
 * based on the type of the binding.
 */
const mapBindingToCompletion = ({
  label,
  value,
  snippet,
  type = 'constant',
  children,
  siblings,
  siblingSeparator = ':',
  isSibling = false,
  quoteType = 'single',
  parentType = 'constant',
  parentQuoteType = 'single',
  ...passThroughProps
}) => {
  // If the binding has a snippet, that will define the apply function
  if (snippet) {
    return snippetCompletion(snippet, { label, type, ...passThroughProps });
  }

  const hasChildren = Array.isArray(children);
  const hasSiblings = Array.isArray(siblings);
  const quoteChar = getQuoteChar(quoteType);
  const parentQuoteChar = getQuoteChar(parentQuoteType);

  // Build the insertion content by using the value or label.
  const content = `${value || label}`;
  // If this completion is inside the operators on its parent determine what
  // the closing operator is for the parent so that we can break out of it if
  // it exists immediately after the insertion point.
  const parentCloseOperator =
    !hasSiblings && ['function', 'object'].includes(parentType)
      ? `${
          isSibling && siblingSeparator === '[]' ? ']' : ''
        }${parentQuoteChar}${parentType === 'function' ? ')' : ']'}`
      : '';
  // Build the operators for this completion if needed
  const contentOperators = hasSiblings
    ? siblingSeparator
    : type === 'function'
      ? `(${quoteChar}${quoteChar})`
      : type === 'object'
        ? `[${quoteChar}${quoteChar}]`
        : type === 'dot-object'
          ? '.'
          : '';
  // Determine if we need to shift the cursor to be between the operators
  const cursorShift = hasSiblings
    ? siblingSeparator === '[]'
      ? -1
      : 0
    : ['function', 'object'].includes(type)
      ? -1 - quoteChar.length
      : 0;

  return {
    label,
    apply: applyFnComplex({
      content,
      parentCloseOperator,
      contentOperators,
      cursorShift,
      triggerAgain:
        // Trigger again if there are siblings or children
        hasSiblings ||
        hasChildren ||
        // Don't trigger for constants or if quoteType is none because we only
        // support quoted completions in functions and objects.
        (type !== 'constant' && quoteType !== 'none'),
    }),
    type,
    ...passThroughProps,
  };
};

// The regular expression used to match a potentially long chain of expressions
// that can correspond to a nested bindings structure.
const fullChainExpressionRegExp = new RegExp(
  // Match the starting word
  /(?<base>[\w@$]+)/.source +
    // Match all completed parens and brackets, and dot notation. Allow any
    // string inside parens and brackets. Allow a word after dot.
    //   parens: \((?:'[^\n']*'|"[^\n"]*")?\)
    //     example: ('abc') | ("abc[def]")
    //   brackets: \[(?:'[^\n']*'|"[^\n"]*")?\]
    //     example: ['abc'] | ["abc def"]
    //   dot notation: \.[\w@$]*
    //     example: .abc
    /(?<parts>(?:\((?:'[^\n']*'|"[^\n"]*")?\)|\[(?:'[^\n']*'|"[^\n"]*")?\]|\.[\w@$]*)*?)??/
      .source +
    // Match open parens or brackets
    //   parens/brackets: [\(\[]['"][\s\w@$-]*(?:[\[:][\s\w@$-]*
    //     example: ('abc | ("abc[def | ['abc:def
    //   dot notation: \.[\w@$]*
    //     example: .abc
    /(?<query>(?:[\(\[]['"][\s\w@$-]*(?:[\[:][\s\w@$-]*)?|\.[\w@$]*))?/.source +
    // End of string to match to the end
    /$/.source,
);

// Function used by the autocompletion code that determines what completions are
// available based on the text preceding the cursor position.
// If force is true, the autocompletions will trigger on input even if there is
// no content preceding the cursor position.
export const buildAutocompletions = (bindings, force = false) => context => {
  if (!Array.isArray(bindings)) return;

  // Match the word immediately before the cursor
  const word = context.matchBefore(/[\w@$]+/);
  // Match a full chain of expressions before the cursor
  const full = context.matchBefore(fullChainExpressionRegExp);

  if (full) {
    // Parse the matched text fo the full expression. Will return:
    // base - the starting word of the expression
    // parts - closed operators in the middle of the expression
    // query - open (last) operator of the expression
    const [, base, parts = '', query] =
      full.text.match(fullChainExpressionRegExp) || [];

    // Return no completions if there is no base value in the match, or no match
    if (!base) return;

    // Separate the parts of the match into an array of individual operators
    const middleParts = Array.from(
      parts.matchAll(
        /\((?:'[^\n']*'|"[^\n"]*")?\)|\[(?:'[^\n']*'|"[^\n"]*")?\]|\.[\w@$]*/g,
      ),
    ).map(match => match[0]);
    // Get the part of expression that's currently being autocompleted, which
    // will be the query, or the base if there are no parts and query.
    const qualification = query || (!parts ? base : '');

    // If there is a qualification, parse the query to figure out which
    // completions to show
    if (qualification) {
      // Get the type of the parent of the query (are we auto completing inside
      // a function or object)
      const parentType = getParentType(qualification);
      // Parse the query to get the individual pieces
      // qBase - the text of the query, excluding sibling part
      // qSeparator - the separator if we're in the sibling part of a query
      // qSibling - this text of the sibling part of the query
      const [, qBase, qSeparator, qSibling] = qualification.match(
        !parentType
          ? // If no parent type, then it's just a word
            /(?<qBase>[\w@$]*)$/
          : // If type is dot-object, qualification starts with a dot, followed by a word
            parentType === 'dot-object'
            ? /\.(?<qBase>[\w@$]*)$/
            : // Otherwise, qualification starts with parens or a bracket, followed by a
              // quote, then any content, and optionally ending with a colon or
              // bracket with more content after
              // Examples: ['kapp | ('attribute:Na | ('field[Sta
              /[\(\[]['"](?<qBase>[\s\w@$-]*)(?:(?<qSeparator>[:\[])(?<qSibling>[\s\w@$-]*))?$/,
      );

      // Convert the matched text to a hierarchy that can be used to navigate the
      // bindings to find any completions for the current spot. The hierarchy
      // will be an array of objects with a label and type.
      const hierarchy = [!!query || !!parts ? base : null, ...middleParts]
        .filter(Boolean)
        // Reduce from the right because the child part tells us what the type
        // is of the parent
        .reduceRight(
          (reduction, part) => {
            const nextParentType = getParentType(part);
            // If there is no next parent type, then we're at the base of the
            // hierarchy, so return the current part in full
            if (!nextParentType) {
              return {
                result: [
                  { label: part, type: reduction.currentType },
                  ...reduction.result,
                ],
              };
            }
            // Otherwise we're in the middle of the hierarchy
            else {
              // Parse the part to separate the text from the wrappers (like
              // parens and brackets), and to check if the part has a sibling,
              // which we will ignore here since those branch off separately
              // and are not part of the regular hierarchy.
              const [, pBase] = part.match(
                nextParentType === 'dot-object'
                  ? /\.(?<pBase>[\w@$]*)$/
                  : /[\(\[]['"](?<pBase>[\s\w@$-]*)(?:[:\[][\s\w@$-]*\]?)?['"][\)\]]$/,
              );
              return {
                currentType: nextParentType,
                result: [
                  {
                    label: pBase || part,
                    type: reduction.currentType,
                  },
                  ...reduction.result,
                ],
              };
            }
          },
          // If the qualification is in the sibling part, add the base of the
          // qualification as the bottom most level of the hierarchy, and set
          // the isSibling property to true
          {
            currentType: parentType,
            result: !!qSeparator
              ? [
                  {
                    label: qBase,
                    type: null,
                    isSibling: true,
                  },
                ]
              : [],
          },
        ).result;

      // Get the available bindings for the current hierarchy by reducing the
      // bindings through their children
      const availableBindings = hierarchy
        .reduce((currentBindings, level) => {
          if (Array.isArray(currentBindings) && currentBindings.length > 0) {
            return currentBindings
              .filter(
                binding =>
                  binding.label === level.label &&
                  (!level.type || binding.type === level.type) &&
                  (!level.isSibling
                    ? Array.isArray(binding.children)
                    : Array.isArray(binding.siblings)),
              )
              .flatMap(
                binding =>
                  !level.isSibling ? binding.children : binding.siblings,
              );
          }
          return [];
        }, bindings)
        // Check if we want to collapse any bindings with siblings
        .flatMap(binding => {
          // For each binding, check if we should collapse the siblings with
          // their parent into a single level
          if (
            // collapseSiblings must be true and
            binding.collapseSiblings &&
            // siblings must exist and
            Array.isArray(binding.siblings) &&
            // The current qualification is empty or
            (!qBase ||
              // The current qualification matches one of the siblings or
              binding.siblings.some(sibling =>
                new RegExp((qBase.length === 1 ? '^' : '') + qBase, 'i').test(
                  sibling.label,
                ),
              ) ||
              // The current qualification doesn't match the parent label
              !new RegExp((qBase.length === 1 ? '^' : '') + qBase, 'i').test(
                binding.label,
              ))
          ) {
            return binding.siblings.map(sibling => ({
              ...sibling,
              // Compose the value by combining the parent with the sibling,
              // separated with the siblingSeparator
              value: `${binding.value || binding.label}${
                binding.siblingSeparator === '[]' ? '[' : ':'
              }${sibling.value || sibling.label}${
                binding.siblingSeparator === '[]' ? ']' : ''
              }`,
            }));
          }
          return [binding];
        });

      // Return the completions
      return {
        from:
          full.to -
          // Subtract the base of the active qualification part
          (!qSeparator ? qBase : qSibling).length,
        // Map the flat bindings to available completions
        options: availableBindings.map(mapBindingToCompletion),
      };
    }
    // If there is no qualification but there are completed parts, see if
    // further chaining is possible
    else if (!!parts) {
      // Convert the matched text to a hierarchy that can be used to navigate
      // the bindings to find the current completion to see if it has children.
      const hierarchy = [base, ...middleParts]
        .filter(Boolean)
        // Reduce from the right because the child part tells us what the type
        // is of the parent
        .reduceRight(
          (reduction, part) => {
            const nextParentType = getParentType(part);
            // If there is no next parent type, then we're at the base of the
            // hierarchy, so return the current part in full
            if (!nextParentType) {
              return {
                result: [
                  { label: part, type: reduction.currentType },
                  ...reduction.result,
                ],
              };
            }
            // Otherwise we're in the middle of the hierarchy
            else {
              // Parse the part to separate the text from the wrappers (like
              // parens and brackets), and to check if the part has a sibling,
              // which we will ignore here since those branch off separately
              // and are not part of the regular hierarchy.
              const [, pBase] =
                part.match(
                  nextParentType === 'dot-object'
                    ? /\.(?<pBase>[\w@$]*)$/
                    : /[\(\[]['"](?<pBase>[\s\w@$-]*)(?:[:\[][\s\w@$-]*\]?)?['"][\)\]]$/,
                ) || [];
              return {
                currentType: nextParentType,
                result: [
                  { label: pBase || part, type: reduction.currentType },
                  ...reduction.result,
                ],
              };
            }
          },
          {
            // Start with an undefined type as we don't know what it is
            currentType: undefined,
            result: [],
          },
        ).result;

      // Get the completion matching the right most part of the hierarchy,
      // which had its type set to undefined
      const lastCompletion = hierarchy.reduce((currentBindings, level) => {
        if (Array.isArray(currentBindings) && currentBindings.length > 0) {
          return currentBindings
            .filter(
              binding =>
                binding.label === level.label &&
                (!level.type ||
                  (binding.type === level.type &&
                    Array.isArray(binding.children))),
            )
            .flatMap(binding => (!level.type ? [binding] : binding.children));
        }
        return [];
      }, bindings)[0];

      // Get the last part to use in the label
      const lastPart = middleParts.slice(-1)[0] || '';

      // If the last completion was an object, function, or dot-object, show a
      // completion for chaining the next part
      if (lastCompletion) {
        if (['function', 'object'].includes(lastCompletion.type)) {
          const startOp = lastCompletion.type === 'function' ? '(' : '[';
          const endOp = lastCompletion.type === 'function' ? ')' : ']';
          const quoteChar = getQuoteChar(lastCompletion.quoteType);
          return {
            from: full.to,
            options: [
              {
                label: `${lastPart}${startOp}${quoteChar}...${quoteChar}${endOp}`,
                apply: applyFnComplex({
                  content: '',
                  contentOperators: `${startOp}${quoteChar}${quoteChar}${endOp}`,
                  cursorShift: -1 - quoteChar.length,
                  triggerAgain: true,
                }),
              },
            ].map(mapBindingToCompletion),
          };
        } else if (lastCompletion.type === 'dot-object') {
          return {
            from: full.to,
            options: [
              {
                label: `${lastPart}.`,
                apply: applyFnComplex({
                  content: '',
                  contentOperators: `.`,
                  cursorShift: 0,
                  triggerAgain: true,
                }),
              },
            ].map(mapBindingToCompletion),
          };
        }
      }
    }
  }

  // If the full expression code block didn't return the available completions,
  // define completions from the top level of the bindings.
  if (word || context.explicit || force) {
    return {
      from: word ? word.from : context.pos,
      options: bindings.map(mapBindingToCompletion),
    };
  }
};

export const buildJsTemplateAutocompletions = bindings => context => {
  const word = context.matchBefore(/(\w|\$)+/);
  const token = context.tokenBefore(['TemplateExpr']);
  const isInEmptyExpression =
    token && token.text.startsWith('${') && !token.text.endsWith('}');

  if (isInEmptyExpression) {
    // If we're inside an empty expression, the expression doesn't have any
    // content yet so the JS parser hasn't kicked in yet. Return the JS
    // expression autocompletions.
    return buildAutocompletions(bindings, true)(context);
  } else if (word || context.explicit) {
    return {
      from: word ? word.from : context.pos,
      options: [
        {
          // eslint-disable-next-line no-template-curly-in-string
          label: '${...}',
          detail: 'Expression',
          apply: applyFnComplex({
            content: '$',
            contentOperators: '{}',
            cursorShift: -1,
            triggerAgain: true,
          }),
        },
      ],
    };
  }
};

export const buildRubyTemplateAutocompletions = bindings => context => {
  const word = context.matchBefore(/(\w|\<)+/);
  const token = context.tokenBefore(['TemplateExpr']);
  const isInExpression =
    token && token.text.startsWith('<%') && !token.text.endsWith('%>');

  if (isInExpression) {
    // If we're inside an expression, show the Ruby expression autocompletions.
    return buildAutocompletions(bindings)(context);
  } else if (word || context.explicit) {
    return {
      from: word ? word.from : context.pos,
      options: [
        {
          label: '<%= ... %>',
          detail: 'Expression',
          apply: applyFnComplex({
            content: '',
            contentOperators: '<%=  %>',
            cursorShift: -3,
            triggerAgain: true,
          }),
        },
      ],
    };
  }
};

// Determines the type of a parent binding by looking at the operators at the
// start of the child string.
function getParentType(query) {
  return query.startsWith('(')
    ? 'function'
    : query.startsWith('[')
      ? 'object'
      : query.startsWith('.')
        ? 'dot-object'
        : null;
}

// Returns the quote character to be used inside function parens or object
// brackets, based on the quoteType param.
function getQuoteChar(quoteType) {
  switch (quoteType) {
    case 'double':
      return '"';
    case 'none':
      return '';
    default:
      return "'";
  }
}
